/** 認証スコープごとの事件種別名。古い事務所・保存前の応答は適用しない。 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch } from '@/api/client';
import { useAuth } from './useAuth';
import { usePermission } from './usePermission';
import {
  resolveCaseTypeNames,
  type CaseTypeNames,
} from '../../../backend/lambdas/shared/case-type-labels';
export type { CaseTypeNames } from '../../../backend/lambdas/shared/case-type-labels';

interface CaseTypeNamesContext {
  names: CaseTypeNames;
  ready: boolean;
  loading: boolean;
  error: string | null;
  reload: () => void;
  save: (labelOverrides: Record<string, string>, expectedVersion: number) => Promise<void>;
  requireReady: () => void;
}
const defaults = resolveCaseTypeNames();
const Context = createContext<CaseTypeNamesContext | null>(null);

export function CaseTypeNamesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const permission = usePermission();
  const scope = user ? `${user.orgId}/${user.userId}` : '';
  const endpoint = permission.canRead('collections')
    ? '/cases/types'
    : permission.canRead('orgSettings')
      ? '/admin/settings/case-type-labels'
      : null;
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const generation = useRef(0);
  const [state, setState] = useState<{
    scope: string;
    names: CaseTypeNames | null;
    error: string | null;
  }>({ scope: '', names: null, error: null });
  const [trigger, setTrigger] = useState(0);
  const reload = useCallback(() => setTrigger((value) => value + 1), []);
  useEffect(() => {
    const request = ++generation.current;
    if (!scope || !endpoint) return;
    setState({ scope, names: null, error: null });
    void (
      endpoint === '/cases/types'
        ? apiFetch<CaseTypeNames>('/cases/types')
        : apiFetch<CaseTypeNames>('/admin/settings/case-type-labels')
    )
      .then((names) => {
        if (request === generation.current && scope === currentScope.current)
          setState({ scope, names, error: null });
      })
      .catch((error: unknown) => {
        if (request === generation.current && scope === currentScope.current)
          setState({
            scope,
            names: null,
            error:
              error instanceof Error ? error.message : '事件種別の表示名を取得できませんでした。',
          });
      });
    return () => {
      generation.current += 1;
    };
  }, [scope, endpoint, trigger]);
  const names = state.scope === scope ? state.names : null;
  const error = state.scope === scope ? state.error : null;
  const requireReady = useCallback(() => {
    if (!names || error || scope !== currentScope.current)
      throw new Error('事件種別の表示名を取得してから再度お試しください。');
  }, [names, error, scope]);
  const save = async (labelOverrides: Record<string, string>, expectedVersion: number) => {
    requireReady();
    const savedScope = scope;
    // 保存開始前の取得応答を無効にする。
    const request = ++generation.current;
    const updated = await apiFetch<CaseTypeNames>('/admin/settings/case-type-labels', {
      method: 'PUT',
      body: JSON.stringify({ labelOverrides, expectedVersion }),
    });
    if (savedScope === currentScope.current && request === generation.current)
      setState({ scope: savedScope, names: updated, error: null });
  };
  return (
    <Context.Provider
      value={{
        names: names ?? defaults,
        ready: !!names && !error,
        loading: !!scope && !!endpoint && !names && !error,
        error,
        reload,
        save,
        requireReady,
      }}
    >
      {children}
    </Context.Provider>
  );
}

const unboundContext: CaseTypeNamesContext = {
  names: defaults,
  ready: true,
  loading: false,
  error: null,
  reload: () => {},
  save: async () => {
    throw new Error('表示名設定を読み込めませんでした。');
  },
  requireReady: () => {},
};

export function useCaseTypeNames(): CaseTypeNamesContext {
  // Providerを置かない単体の表示コンポーネントでは従来の既定表示を維持する。
  return useContext(Context) ?? unboundContext;
}
